import { useEffect, useState, type JSX } from 'react';
import type { FilterInstance, Id } from '../../../core/model';
import type { FilterOwner } from '../../../state/api';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { InlineEdit } from '../../components/InlineEdit';
import { ViewCanvas } from '../../components/ViewCanvas';
import { useProject, useRuntime, useUiContext } from '../../context';
import { useEditSession } from '../../hooks/useEditSession';
import { attempt } from '../../safe';
import { describe } from '../properties/PropertiesDialog';
import { PropertyForm } from '../properties/PropertyForm';
import { moveWithinType, splitFilters, type FilterType } from './filterLists';
import '../dialogs.css';

const EMPTY: readonly FilterInstance[] = [];

interface ListProps {
  type: FilterType;
  owner: FilterOwner;
  all: readonly FilterInstance[];
  filters: FilterInstance[];
  selected: Id | null;
  onSelect(id: Id | null): void;
}

function FilterList({ type, owner, all, filters, selected, onSelect }: ListProps): JSX.Element {
  const { studio, ui } = useUiContext();
  const { commands, registry } = studio;
  const [renaming, setRenaming] = useState<Id | null>(null);
  const title = type === 'video' ? 'Effect filters' : 'Audio filters';
  const current = filters.find((f) => f.id === selected) ?? null;

  const move = (step: 1 | -1): void => {
    if (!current) return;
    const to = moveWithinType(registry, all, current.id, step);
    if (to !== null) attempt(studio, () => commands.moveFilter(owner, current.id, to));
  };

  const openAdd = (anchor: HTMLElement): void => {
    const rect = anchor.getBoundingClientRect();
    const defs = registry.listFilters(type).sort((a, b) => a.displayName.localeCompare(b.displayName));
    ui.getState().openMenu({
      x: rect.left,
      y: rect.bottom + 2,
      returnFocus: anchor,
      items:
        defs.length > 0
          ? defs.map((def) => ({
              label: def.displayName,
              onSelect: () => {
                const id = attempt(studio, () => commands.addFilter(owner, def.kind));
                if (id) onSelect(id);
              },
            }))
          : [{ label: `No ${type} filters are registered`, disabled: true }],
    });
  };

  return (
    <div className="filter-list">
      <div className="filter-list-title">{title}</div>
      <ul
        className="ui-list ui-grow"
        role="listbox"
        aria-label={title}
        tabIndex={0}
        style={{ overflow: 'auto' }}
        onKeyDown={(e) => {
          if (renaming || !current) return;
          if (e.key === 'F2') setRenaming(current.id);
          else if (e.key === 'Delete') attempt(studio, () => commands.removeFilter(owner, current.id));
          else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const index = filters.indexOf(current) + (e.key === 'ArrowDown' ? 1 : -1);
            if (filters[index]) onSelect(filters[index].id);
          } else return;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {filters.map((filter) => (
          <li
            key={filter.id}
            role="option"
            aria-selected={filter.id === selected}
            className={filter.id === selected ? 'ui-list-row selected' : 'ui-list-row'}
            onClick={() => onSelect(filter.id)}
            onDoubleClick={() => setRenaming(filter.id)}
          >
            <input
              type="checkbox"
              checked={filter.enabled}
              aria-label={`Enable ${filter.name}`}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => attempt(studio, () => commands.updateFilter(owner, filter.id, { enabled: e.target.checked }))}
            />
            {renaming === filter.id ? (
              <InlineEdit
                initial={filter.name}
                label="Filter name"
                onSubmit={(name) => {
                  attempt(studio, () => commands.updateFilter(owner, filter.id, { name }));
                  setRenaming(null);
                }}
                onCancel={() => setRenaming(null)}
              />
            ) : (
              <span className="ui-grow ui-ellipsis">{filter.name}</span>
            )}
            {registry.getFilter(filter.kind) ? null : <Icon name="warning" title="Filter type is not available" />}
          </li>
        ))}
      </ul>
      <div className="ui-toolbar" role="toolbar" aria-label={`${title} actions`}>
        <button type="button" className="ui-icon-btn" aria-label={`Add ${type} filter`} aria-haspopup="menu" title="Add filter" onClick={(e) => openAdd(e.currentTarget)}>
          <Icon name="plus" />
        </button>
        <button
          type="button"
          className="ui-icon-btn"
          aria-label={`Remove ${type} filter`}
          title="Remove filter"
          disabled={!current}
          onClick={() => current && attempt(studio, () => commands.removeFilter(owner, current.id))}
        >
          <Icon name="minus" />
        </button>
        <span className="ui-grow" />
        <button type="button" className="ui-icon-btn" aria-label={`Move ${type} filter up`} title="Move up" disabled={!current} onClick={() => move(-1)}>
          <Icon name="up" />
        </button>
        <button type="button" className="ui-icon-btn" aria-label={`Move ${type} filter down`} title="Move down" disabled={!current} onClick={() => move(1)}>
          <Icon name="down" />
        </button>
      </div>
    </div>
  );
}

export function FiltersDialog({ owner, onClose }: { owner: FilterOwner; onClose(): void }): JSX.Element | null {
  const { studio } = useUiContext();
  const { commands, registry } = studio;
  const target = useProject((p) => (owner.type === 'source' ? p.sources[owner.id] : p.scenes[owner.id]));
  const kind = useProject((p) => (owner.type === 'source' ? p.sources[owner.id]?.kind : undefined));
  const runtimeInfo = useRuntime((r) => (owner.type === 'source' ? r.sources[owner.id] : undefined));
  const [selected, setSelected] = useState<Id | null>(null);
  const session = useEditSession(studio);

  const exists = target !== undefined;
  useEffect(() => {
    if (!exists) onClose();
  }, [exists, onClose]);
  if (!target) return null;

  const filters = target.filters ?? EMPTY;
  const lists = splitFilters(registry, filters);
  const caps = kind ? registry.getSource(kind)?.caps : undefined;
  const showVideo = owner.type === 'scene' || (runtimeInfo?.hasVideo ?? caps?.video ?? true) || lists.video.length > 0;
  const showAudio = owner.type === 'source' && ((runtimeInfo?.hasAudio ?? caps?.audio ?? false) || lists.audio.length > 0);

  const current = filters.find((f) => f.id === selected) ?? null;
  const def = current ? registry.getFilter(current.kind) : undefined;
  const form = current && def ? describe(() => def.properties(current.settings)) : null;
  const seal = (): void => session.commit();
  const filterSetting = (filterId: Id, key: string): unknown => {
    const project = studio.project.getState().project;
    const owned = owner.type === 'source' ? project.sources[owner.id]?.filters : project.scenes[owner.id]?.filters;
    return owned?.find((f) => f.id === filterId)?.settings[key];
  };

  const close = (): void => {
    seal();
    onClose();
  };

  return (
    <Dialog
      title={`Filters for "${target.name}"`}
      onClose={close}
      width={860}
      height={600}
      flush
      footer={
        <>
          <button
            type="button"
            className="ui-btn"
            style={{ marginRight: 'auto' }}
            disabled={!current || !def}
            onClick={() => {
              if (!current || !def) return;
              seal();
              attempt(studio, () => commands.updateFilter(owner, current.id, { settings: def.defaults() }));
              seal();
            }}
          >
            Defaults
          </button>
          <button type="button" className="ui-btn primary" onClick={close}>
            Close
          </button>
        </>
      }
    >
      <div className="dialog-split">
        <div className="filter-lists">
          {showAudio ? <FilterList type="audio" owner={owner} all={filters} filters={lists.audio} selected={selected} onSelect={setSelected} /> : null}
          {showVideo ? <FilterList type="video" owner={owner} all={filters} filters={lists.video} selected={selected} onSelect={setSelected} /> : null}
        </div>
        <div className="dialog-column">
          {showVideo ? (
            <ViewCanvas
              target={owner.type === 'source' ? { type: 'source', sourceId: owner.id } : { type: 'scene', sceneId: owner.id }}
              label={`${target.name} preview`}
              className="dialog-preview"
            />
          ) : null}
          <div className="dialog-scroll">
            {!current ? <div className="ui-empty">Select a filter to edit it, or add one with +.</div> : null}
            {current && !def ? <div className="ui-warn-text">The filter type "{current.kind}" is not available (is its plugin loaded?).</div> : null}
            {form?.error ? <div className="ui-error-text">Could not build the settings form: {form.error}</div> : null}
            {current && form && !form.error ? (
              <PropertyForm
                key={current.id}
                descriptors={form.descriptors}
                settings={current.settings}
                onChange={(key, value) =>
                  session.edit(
                    `${current.id}:${key}`,
                    () => filterSetting(current.id, key),
                    (next, options) => commands.updateFilter(owner, current.id, { settings: { [key]: next } }, options),
                    value,
                  )
                }
                onCommit={seal}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
